import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import { SFButton } from "../buttons";
import { SFStack } from "../grid";
import SFUseStyled from "../../hooks/useStyled";
import SFNotificationPanel from "../notification";
import { markNotificationsAsRead } from "../../services/notificationService";
import { notificationActions } from "../../redux/reducers/notificationReducer";
import SFAvatar from "../avatar";
import SFTypography from "../typography";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const { styled } = SFUseStyled();

const StyledNewNotificationsBadge = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  right: 0,
  width: 12,
  height: 12,
  borderRadius: "50%",
  backgroundColor: theme.palette.yellow400.main,
  border: "1px solid black",
}));

const AvatarContainer = styled("div")(({ theme }) => ({
  cursor: "pointer",
  position: "relative",
  paddingRight: theme.spacing(0.75),
}));

const SFAvatarStyled = styled(SFAvatar)(() => ({
  width: 35,
  height: 35,
  fontSize: 20,
}));

const StyledLine = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 240,
  height: "79px",
  width: "1px",
  zIndex: 999,
  borderRight: `1px solid ${theme.palette.grey450.main}`,
}));

const SFAppBar = ({
  logo,
  items = [],
  itemsRight = [],
  disableGradient = false,
  dividerLine = false,
  showUserAvatar = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const notificationReduxState = useSelector((state) => state.notification);
  const userAvatar = useSelector((state) => state.session.avatar);
  const userName = useSelector((state) => state.userConfiguration.name);
  const firstName = useSelector((state) => state.userConfiguration.firstName);
  const showNotificationPanel = useSelector(
    (state) => state.notification.showNotificationPanel
  );
  const notifications = notificationReduxState.notifications;

  //const showProfilePage = location.pathname.includes(LPUrls.getProfileUrl());

  const openNotificationsPanelHandler = () => {
    dispatch(notificationActions.setNotificationPanel(true));
  };

  const closeNotificationsPanelHandler = () => {
    dispatch(notificationActions.setNotificationPanel(false));
  };

  useEffect(() => {
    //if panel is opening and there are unread messages
    //then mark them as read both in redux and api
    if (showNotificationPanel && notifications.some((x) => !x.read)) {
      markNotificationsAsRead();
      dispatch(notificationActions.markNotificationsAsRead());
    }
  }, [showNotificationPanel]);

  const getIsItemActive = (item) => {
    if (item.path && location.pathname.startsWith(item.path)) {
      return true;
    }
    return false;
  };

  return (
    <AppBar
      position="fixed"
      sx={{ minHeight: 98 }}
      disable-gradient={disableGradient.toString()}
    >
      {dividerLine && <StyledLine />}
      <Toolbar sx={{ lineHeight: 67, display: "flex" }}>
        {logo}
        <Box
          sx={{
            ml: 5,
            paddingTop: "22px",
            flexGrow: 1,
            alignItems: "flex-end",
            height: "100%",
            display: "flex",
            justifyContent: "space-between",
            //display: { xs: "none", md: "flex" },
          }}
        >
          <SFStack direction="row">
            {items.map((item) => (
              <SFButton
                type="button"
                variant="toolbar"
                className={getIsItemActive(item) ? "active" : ""}
                key={item.title}
                {...(item.icon && {
                  startIcon: item.icon,
                })}
                {...(item.onClick && {
                  onClick: item.onClick,
                })}
                {...(item.onMouseOver && {
                  onMouseOver: item.onMouseOver,
                })}
                {...(item.onMouseOut && {
                  onMouseOut: item.onMouseOut,
                })}
              >
                {item.title}
              </SFButton>
            ))}
          </SFStack>
          <SFStack direction="row">
            {itemsRight.length > 0 && (
              <SFStack direction="row">
                {itemsRight.map((item) => (
                  <SFButton
                    type="button"
                    variant="toolbar"
                    key={item.title}
                    {...(item.icon && {
                      startIcon: item.icon,
                    })}
                    {...(item.onClick && {
                      onClick: item.onClick,
                    })}
                    {...(item.onMouseOver && {
                      onMouseOver: item.onMouseOver,
                    })}
                    {...(item.onMouseOut && {
                      onMouseOut: item.onMouseOut,
                    })}
                  >
                    {item.title}
                  </SFButton>
                ))}
              </SFStack>
            )}
            {showUserAvatar && (
              <SFButton
                component="label"
                variant="toolbar"
                ariaLabel={t("components.header.notificationPanel.notificationButtonAriaLabel")}
                onClick={openNotificationsPanelHandler}
                startIcon={<AvatarContainer>
                  <SFAvatarStyled src={userAvatar} name={firstName} />
                  {notifications.filter((x) => !x.read).length > 0 && (
                    <StyledNewNotificationsBadge />
                  )}
                </AvatarContainer>}
              >

                {firstName && (
                  <SFTypography variant="cardSectionHeading">
                    {firstName}
                  </SFTypography>
                )}
              </SFButton>
            )}
          </SFStack>
        </Box>
        <SFNotificationPanel
          show={showNotificationPanel}
          onClose={closeNotificationsPanelHandler}
          notifications={notifications}
          onClickNotification={() => { }}
          name={userName}
          avatar={userAvatar}
        />
      </Toolbar>
    </AppBar>
  );
};

export default SFAppBar;
